/** One entry a bidder typed. The id is minted so removing a row cannot shuffle the others. */
export interface Segment {
  id: string;
  value: string;
}

export function newSegment(): Segment {
  return { id: crypto.randomUUID(), value: '' };
}

interface Props {
  label: string;
  /** 'ft' for a roofline, 'strands' for an accent feature. */
  unit: string;
  hint: string;
  segments: Segment[];
  total: number;
  onChange(segments: Segment[]): void;
}

/**
 * A repeatable numeric entry: type a number, press + for another, and the running
 * total is what gets priced.
 *
 * This exists because a roofline is measured in pieces. Adding 50, 40 and 10 in the
 * form beats doing it in your head on a driveway and typing 100, and keeping the
 * pieces means the bid records how the number was arrived at.
 *
 * Values are held as strings so a half-typed "12." is not clobbered, matching the
 * convention the rest of this form uses. Blank and zero rows are ignored rather
 * than blocking the bid.
 */
export function SegmentRows({ label, unit, hint, segments, total, onChange }: Props) {
  const rows = segments.length > 0 ? segments : [newSegment()];
  const filled = rows.filter((s) => s.value.trim() !== '').length;

  function setValue(id: string, value: string) {
    onChange(rows.map((s) => (s.id === id ? { ...s, value } : s)));
  }

  return (
    <div className="segblock">
      <div className="seghead">
        <span className="label">{label}</span>
        <span className="unit">{hint}</span>
      </div>

      {rows.map((segment, i) => (
        <div className="segrow" key={segment.id}>
          <input
            type="number"
            inputMode={unit === 'ft' ? 'decimal' : 'numeric'}
            min="0"
            step={unit === 'ft' ? '0.5' : '1'}
            placeholder={unit}
            aria-label={`${label} ${unit}, entry ${i + 1}`}
            value={segment.value}
            onChange={(e) => setValue(segment.id, e.target.value)}
          />
          <button
            type="button"
            className="ghost"
            aria-label={`Add another ${label} entry`}
            onClick={() => {
              const next = [...rows];
              next.splice(i + 1, 0, newSegment());
              onChange(next);
            }}
          >
            +
          </button>
          <button
            type="button"
            className="ghost"
            aria-label={`Remove ${label} entry ${i + 1}`}
            // Never leave the group with nothing to type into.
            disabled={rows.length === 1}
            onClick={() => onChange(rows.filter((s) => s.id !== segment.id))}
          >
            ✕
          </button>
        </div>
      ))}

      {filled > 1 && (
        <div className="segtotal">
          {filled} entries · <strong>{total}</strong> {unit}
        </div>
      )}
    </div>
  );
}
